import { createFeatureSelector, createSelector, select } from '@ngrx/store';
import { getThemeAwareColor as canonicalGetThemeAwareColor } from '@bepatient/shared';
import { pipe } from 'rxjs';
import { distinctUntilChanged, map } from 'rxjs/operators';
import { CoreActions, CoreActionsTypes } from './core.actions';
import { environment } from 'apps/smart-app/src/environments/environment';
import {
  CoreInstance,
  CoreInstanceEnvironment,
  CoreInstanceInfo,
  CoreInstancesData,
  CoreLogin,
  CoreMeasure,
  CoreMenu,
  CoreMobiResourceType,
  CorePageModule,
  CorePageModuleIDs,
  CoreResourceFormOverride,
  CoreResourceStackItem,
  CoreUser
} from './core.models';

export interface CoreState {
  appVersion: string;
  entryPageModulesIDs: Array<CorePageModuleIDs>;
  instancesData: CoreInstancesData;
  instanceEnv: CoreInstanceEnvironment;
  instanceInfo: CoreInstanceInfo;
  login: CoreLogin;
  loginSuccess: boolean;
  menu: CoreMenu;
  navigateToSingleResourceHref: string;
  navigateToResourceStack: Array<CoreResourceStackItem>;
  navigateToResourceFormOverride: CoreResourceFormOverride;
  pageModules: Array<CorePageModule>;
  resetPasswordSuccess: boolean;
  rootPageModuleID: CorePageModuleIDs;
  useGlobalLoading: boolean;
  user: CoreUser;
  fcmToken: string;
  notificationTap: boolean;
  previousAttemptingUrl: string;
  measureToTake: CoreMeasure;
  confirmPasswordFailed: boolean;
  isPlatformInitialized: boolean;
  groupImage: string;
  nextActivity: boolean;
  currentPageStatus: any;
  menuReloadRequired: boolean;
  isDarkModeEnabled: boolean;
  displayFormErrors: boolean;
  externalLink: string;
  currentFormId: string;
  userSignature: any;
  fetchedDocumentContent: any;
  loadedFormData: any;
  // MYFSHD
  deeplinkFormData: any;
  isAppActive: boolean;
  checkLowBatteryComplete: boolean;
}

export const coreInitialState: CoreState = {
  appVersion: void 0,
  entryPageModulesIDs: [],
  instancesData: void 0,
  instanceEnv: void 0,
  instanceInfo: void 0,
  login: void 0,
  loginSuccess: void 0,
  menu: void 0,
  navigateToSingleResourceHref: void 0,
  navigateToResourceStack: [],
  navigateToResourceFormOverride: void 0,
  pageModules: [],
  resetPasswordSuccess: void 0,
  rootPageModuleID: void 0,
  useGlobalLoading: false,
  user: void 0,
  fcmToken: void 0,
  notificationTap: false,
  previousAttemptingUrl: void 0,
  measureToTake: void 0,
  confirmPasswordFailed: false,
  isPlatformInitialized: false,
  groupImage: void 0,
  nextActivity: false,
  currentPageStatus: void 0,
  menuReloadRequired: false,
  isDarkModeEnabled: false,
  displayFormErrors: false,
  externalLink: void 0,
  currentFormId: void 0,
  userSignature: void 0,
  fetchedDocumentContent: void 0,
  loadedFormData: void 0,
  // MYFSHD
  deeplinkFormData: void 0,
  isAppActive: true,
  checkLowBatteryComplete: false
};

export function coreReducer(
  state = coreInitialState,
  action: CoreActions
): CoreState {
  switch (action.type) {
    case CoreActionsTypes.ADD_PAGE_MODULE:
      return {
        ...state,
        pageModules: [...state.pageModules, action.payload]
      };

    case CoreActionsTypes.LOAD_MENU_SUCCESS:
      return { ...state, menu: action.payload };

    case CoreActionsTypes.LOAD_USER_FAILED:
      return { ...state, user: void 0, loginSuccess: false };

    case CoreActionsTypes.LOAD_USER_SUCCESS:
      return { ...state, user: action.payload, loginSuccess: true };

    case CoreActionsTypes.LOAD_INSTANCE_INFO_SUCCESS:
      const devType = environment.devType.toLowerCase();
      return {
        ...state,
        // Single source of truth for the theme-adjusted display color: derive
        // appColor from the raw instanceColor here, so it can never go stale
        // depending on which API parser (core-api vs instance-store connect)
        // produced the payload. Parsers only carry the raw color.
        instanceInfo: action.payload
          ? {
              ...action.payload,
              appColor: action.payload.instanceColor
                ? canonicalGetThemeAwareColor(action.payload.instanceColor)
                : action.payload.appColor
            }
          : action.payload,
        ...(action?.payload?.isQR && action?.payload?.instance_code
          ? {
              instancesData: {
                ...state.instancesData,
                [devType]: {
                  ...state.instancesData[devType],
                  code: action?.payload?.instance_code
                }
              }
            }
          : {})
      };

    case CoreActionsTypes.LOGIN:
      return {
        ...state,
        login: void 0,
        user: void 0,
        menu: void 0,
        loginSuccess: void 0
      };

    case CoreActionsTypes.LOGIN_FAILED:
      return {
        ...state,
        login: void 0,
        user: void 0,
        menu: void 0,
        loginSuccess: false
      };

    case CoreActionsTypes.LOGIN_SUCCESS:
      return {
        ...state,
        login: {
          ...state.login,
          ...action.payload,
          disagreeTrustedDevice: false
        },
        user: void 0,
        menu: void 0,
        loginSuccess: true
      };

    case CoreActionsTypes.LOGOUT:
      return {
        ...state,
        user: void 0,
        menu: void 0,
        login: void 0,
        instanceEnv: void 0,
        loginSuccess: void 0
      };

    case CoreActionsTypes.NAVIGATE_TO_SINGLE_RESOURCE:
      return {
        ...state,
        navigateToSingleResourceHref: action.payload.href
      };

    case CoreActionsTypes.NAVIGATE_TO_RESOURCE_STACK:
      return {
        ...state,
        navigateToResourceStack: action.payload.asRoot
          ? [action.payload.resource]
          : [...state.navigateToResourceStack, action.payload.resource]
      };

    case CoreActionsTypes.NAVIGATE_TO_RESOURCE_STACK_POP:
      return {
        ...state,
        navigateToResourceStack: state.navigateToResourceStack.slice(0, -1)
      };

    case CoreActionsTypes.NAVIGATE_TO_RESOURCE_STACK_UPDATE_LAST_ITEM:
      const updatedItem = {
        ...state.navigateToResourceStack.pop(),
        ...action.payload
      };
      return {
        ...state,
        navigateToResourceStack: [...state.navigateToResourceStack, updatedItem]
      };

    case CoreActionsTypes.OVERRIDE_RESOURCE_FORM:
      return { ...state, navigateToResourceFormOverride: action.payload };

    case CoreActionsTypes.RESET_LOGIN_SUCCESS:
      return { ...state, loginSuccess: void 0 };

    case CoreActionsTypes.RESET_PASSWORD:
      return { ...state, resetPasswordSuccess: void 0 };

    case CoreActionsTypes.RESET_PASSWORD_FAILED:
      return { ...state, resetPasswordSuccess: false };

    case CoreActionsTypes.RESET_PASSWORD_SUCCESS:
      return { ...state, resetPasswordSuccess: true };

    case CoreActionsTypes.SET_APP_VERSION:
      return { ...state, appVersion: action.payload };

    case CoreActionsTypes.SET_ENTRY_PAGE_MODULES:
      return { ...state, entryPageModulesIDs: action.payload };

    case CoreActionsTypes.SET_INSTANCES_DATA:
      return {
        ...state,
        instancesData: action.payload
      };

    case CoreActionsTypes.SET_INSTANCE_ENV:
      return { ...state, instanceEnv: action.payload };

    case CoreActionsTypes.SET_ROOT_PAGE_MODULE:
      return { ...state, rootPageModuleID: action.payload };

    case CoreActionsTypes.SET_USE_GLOBAL_LOADING:
      return { ...state, useGlobalLoading: action.payload };

    case CoreActionsTypes.UPDATE_LOGIN:
      return {
        ...state,
        login: {
          ...state.login,
          ...action.payload
        }
      };

    case CoreActionsTypes.SET_FCM_TOKEN:
      return {
        ...state,
        fcmToken: action.payload
      };

    case CoreActionsTypes.SET_NOTIFICATION_TAP:
      return {
        ...state,
        notificationTap: action.payload
      };

    case CoreActionsTypes.SET_PREVIOUS_ATTEMPTING_URL:
      return { ...state, previousAttemptingUrl: action.payload };

    case CoreActionsTypes.TAKE_MEASURE:
      return { ...state, measureToTake: action.payload.measure };

    case CoreActionsTypes.CONFIRM_PASSWORD_FAILED:
      return { ...state, confirmPasswordFailed: action.payload };

    case CoreActionsTypes.PLATFORM_INITIALIZED:
      return { ...state, isPlatformInitialized: true };

    case CoreActionsTypes.SET_GROUP_IMAGE:
      return { ...state, groupImage: action.payload };

    case CoreActionsTypes.GOTO_NEXT_ACTIVITY:
      return { ...state, nextActivity: action.payload ? true : false };

    case CoreActionsTypes.SET_FORM_CURRENT_PAGE_STATUS:
      return {
        ...state,
        currentPageStatus: {
          ...state.currentPageStatus,
          ...action.payload
        }
      };

    case CoreActionsTypes.SET_MENU_RELOAD_REQUIRED:
      return {
        ...state,
        menuReloadRequired: action.payload
      };

    case CoreActionsTypes.DETECT_THEME_SUCCESS:
      return {
        ...state,
        isDarkModeEnabled: action.payload
      };

    case CoreActionsTypes.DISPLAY_FORM_ERRORS:
      return {
        ...state,
        displayFormErrors: action.payload
      };

    case CoreActionsTypes.DISAGREE_TRUSTED_DEVICE:
      return {
        ...state,
        login: {
          ...state.login,
          disagreeTrustedDevice: action.payload
        }
      };

    case CoreActionsTypes.OPEN_EXTERNAL_LINK:
      return {
        ...state,
        externalLink: action.payload
      };

    case CoreActionsTypes.FETCH_DOCUMENT_CONTENT:
      return {
        ...state,
        fetchedDocumentContent: action.payload
      };

    case CoreActionsTypes.SET_USER_SIGNATURE_SUCCESS:
      return {
        ...state,
        userSignature: action.payload
      };

    case CoreActionsTypes.RESET_USER_SIGNATURE:
      return {
        ...state,
        userSignature: void 0
      };

    case CoreActionsTypes.LOADED_FORM_DATA:
      return {
        ...state,
        loadedFormData: action.payload
      };

    case CoreActionsTypes.SAVE_DEEPLINK_FORM_DATA:
      return {
        ...state,
        deeplinkFormData: action.payload
      };

    case CoreActionsTypes.REMOVE_LOGIN:
      return {
        ...state,
        user: void 0,
        menu: void 0,
        login: void 0,
        instanceEnv: void 0,
        loginSuccess: void 0
      };

    case CoreActionsTypes.SET_APP_ACTIVE_STATUS:
      return {
        ...state,
        isAppActive: action.payload
      };

    case CoreActionsTypes.SET_CURRENT_FORM_ID:
      return {
        ...state,
        currentFormId: action.payload
      };

    case CoreActionsTypes.CHECK_BATTERY_COMPLETE:
      return {
        ...state,
        checkLowBatteryComplete: action.payload
      };

    case CoreActionsTypes.UPDATE_INSTANCE_APP_COLOR:
      return state.instanceInfo
        ? {
            ...state,
            instanceInfo: { ...state.instanceInfo, appColor: action.payload }
          }
        : state;

    default:
      return state;
  }
}

const featureSelectorCoreState = createFeatureSelector<CoreState>('core');

export const selectorCoreAppVersion = createSelector(
  featureSelectorCoreState,
  state => state.appVersion
);
export const selectorCoreEntryPageModulesIDs = createSelector(
  featureSelectorCoreState,
  state => state.entryPageModulesIDs
);
export const selectorCoreInstancesData = createSelector(
  featureSelectorCoreState,
  state => state.instancesData
);
export const selectorCoreInstanceEnv = createSelector(
  featureSelectorCoreState,
  state => state.instanceEnv
);
export const selectorCoreInstanceInfo = createSelector(
  featureSelectorCoreState,
  state => state.instanceInfo
);
export const selectorCoreLogin = createSelector(
  featureSelectorCoreState,
  state => state.login
);
export const selectorCoreLoginSuccess = createSelector(
  featureSelectorCoreState,
  state => state.loginSuccess
);
export const selectorCoreMenu = createSelector(
  featureSelectorCoreState,
  state => state.menu
);
export const selectorCorePageModules = createSelector(
  featureSelectorCoreState,
  state => state.pageModules
);
export const selectorCoreResetPasswordSuccess = createSelector(
  featureSelectorCoreState,
  state => state.resetPasswordSuccess
);
export const selectorCoreRootPageModuleID = createSelector(
  featureSelectorCoreState,
  state => state.rootPageModuleID
);
export const selectorCoreUseGlobalLoading = createSelector(
  featureSelectorCoreState,
  state => state.useGlobalLoading
);
export const selectorCoreUser = createSelector(
  featureSelectorCoreState,
  state => state.user
);
export const selectorCoreFCMToken = createSelector(
  featureSelectorCoreState,
  state => state.fcmToken
);
export const selectorCoreNotificationTap = createSelector(
  featureSelectorCoreState,
  state => state.notificationTap
);
export const selectorCoreNavigateToSingleResourceHref = createSelector(
  featureSelectorCoreState,
  state => state.navigateToSingleResourceHref
);
export const selectorCoreNavigateToResourceStack = createSelector(
  featureSelectorCoreState,
  state => state.navigateToResourceStack
);
export const selectorCorePreviousAttemptingUrl = createSelector(
  featureSelectorCoreState,
  state => state.previousAttemptingUrl
);
export const selectorCoreOverrideResourceForm = createSelector(
  featureSelectorCoreState,
  state => state.navigateToResourceFormOverride
);
export const selectorCoreMeasureToTake = createSelector(
  featureSelectorCoreState,
  state => state.measureToTake
);
export const selectorCoreConfirmPasswordFailed = createSelector(
  featureSelectorCoreState,
  state => state.confirmPasswordFailed
);
export const selectorCorePlatformInitialized = createSelector(
  featureSelectorCoreState,
  state => state.isPlatformInitialized
);
export const selectorCoreGroupImage = createSelector(
  featureSelectorCoreState,
  state => state.groupImage
);

export const selectorCoreNextActivity = createSelector(
  featureSelectorCoreState,
  state => state.nextActivity
);

export const selectorCoreFormCurrentPageStatus = createSelector(
  featureSelectorCoreState,
  state => state.currentPageStatus
);

export const selectorCoreMenuReloadRequired = createSelector(
  featureSelectorCoreState,
  state => state.menuReloadRequired
);

export const selectorCoreDisplayFormErrors = createSelector(
  featureSelectorCoreState,
  state => state.displayFormErrors
);

export const selectorCoreExternalLink = createSelector(
  featureSelectorCoreState,
  state => state.externalLink
);

export const selectorCoreUserSignature = createSelector(
  featureSelectorCoreState,
  state => state.userSignature
);

export const selectorCoreFetchDocumentContent = createSelector(
  featureSelectorCoreState,
  state => state.fetchedDocumentContent
);

export const selectorCoreLoadedFormData = createSelector(
  featureSelectorCoreState,
  state => state.loadedFormData
);

// MYFSHD
export const selectorCoreDeeplinkFormData = createSelector(
  featureSelectorCoreState,
  state => state.deeplinkFormData
);

export const selectorCoreIsAppActive = createSelector(
  featureSelectorCoreState,
  state => state.isAppActive
);

export const selectorCoreCurrentFormId = createSelector(
  featureSelectorCoreState,
  state => state.currentFormId
);

export const selectorLowBatteryCheckComplete = createSelector(
  featureSelectorCoreState,
  state => state.checkLowBatteryComplete
);

// composed

export const selectorCoreSelectedInstance = createSelector(
  selectorCoreInstancesData,
  selectorCoreInstanceEnv,
  (instance, instanceEnv) => {
    switch (instanceEnv) {
      case 'BETA':
        return (instance && instance.beta) || (instance && instance.prod);
      case 'DEV':
        return (instance && instance.dev) || (instance && instance.prod);
      case 'TEST':
        return (instance && instance.test) || (instance && instance.prod);
      default:
        return instance && instance.prod;
    }
  }
);

export const selectorCoreInstanceAndUser = createSelector(
  selectorCoreInstancesData,
  selectorCoreUser,
  (instance, user) => [instance, user]
);

export const selectorCoreNavigateToResourceStackLastItemType = (
  type?: CoreMobiResourceType
) =>
  createSelector(featureSelectorCoreState, state => {
    const stack = type
      ? state.navigateToResourceStack.filter(item => item.type === type)
      : state.navigateToResourceStack;
    return stack.length ? stack[stack.length - 1] : void 0;
  });

export const selectorCoreNavigateToResourceStackPreviousItemData =
  createSelector(featureSelectorCoreState, state => {
    const previousItem =
      state.navigateToResourceStack.length > 1
        ? state.navigateToResourceStack[
            state.navigateToResourceStack.length - 2
          ]
        : void 0;
    return (
      previousItem
        ? {
            even: state.navigateToResourceStack.length % 2 ? false : true,
            color: previousItem.color,
            title: previousItem.title
          }
        : void 0
    ) as { even: boolean; color: string; title: string };
  });

export const selectorCoreMainNavigationData = createSelector(
  selectorCoreMenu,
  selectorCoreUser,
  selectorCoreLogin,
  selectorCoreInstancesData,
  selectorCoreInstanceInfo,
  (menu, user, login, instance, instanceInfo) => ({
    menu,
    user,
    login,
    instance,
    instanceInfo
  })
);

export function selectCoreExtraAppConfiguration(data: any) {
  return createSelector(
    selectorCoreUser,
    selectorCoreLogin,
    selectorCoreInstancesData,
    (
      user: CoreUser,
      loginInfo: CoreLogin,
      instancesData: CoreInstancesData
    ) => {
      const instanceUrl = localStorage.getItem('instanceUrl');
      const options = {
        ...data,
        displayMeasuresInConnect: true,
        token: loginInfo.iAuth,
        tokenCheckFrequency: loginInfo.tokenValidationCheckFrequency,
        tokenWarningExpirationDuration:
          loginInfo.tokenWarningExpirationDuration,
        language: loginInfo.language,
        rightToLeft: loginInfo.rightToLeft,
        userAgent: window.navigator.userAgent,
        instanceUrl: instanceUrl,
        instanceApiURL: data.devType
          ? instancesData[`${data.devType}`]?.url || null
          : null,
        instanceCode: data.devType
          ? instancesData[`${data.devType}`]?.code || null
          : null,
        appPackageName: 'com.bepatient.smartapp',
        appURL: 'com.bepatient.smartapp://'
      };
      return {
        intent: encodeURI(
          `${data.bundleId}://values?${objectToString(options)}`
        ),
        options: {
          package: data.bundleURL
        }
      };
    }
  );
}

export const selectorCoreChatbotAppConfiguration = (props: any) =>
  createSelector(selectorCoreLogin, (loginInfo: CoreLogin) => {
    const options = {
      userID:
        (loginInfo && loginInfo.userId && loginInfo.userId.toString()) || ''
    };
    return {
      intent: encodeURI(
        `extrachatbot://com.bepatient.chatbot/values?${objectToString(options)}`
      )
    };
  });

export const selectorCoreExternalAppConfiguration = (props: any) =>
  createSelector(selectorCoreInstancesData, intance => {
    return {
      intent: {
        action: 'ACTION_MAIN',
        type: 'text/css',
        application: props.application,
        flags: ['FLAG_ACTIVITY_CLEAR_TOP', 'FLAG_ACTIVITY_CLEAR_TASK']
      }
    };
  });

export const selectCorePageModule = (id: CorePageModuleIDs) =>
  pipe(
    select(selectorCorePageModules),
    map(modules => modules || []),
    map(modules => modules.filter(module => module.ID === id)),
    map(modules => (modules.length && modules[0]) || void 0)
  );

// because instance & user are rehydrated

export const selectCoreLoginDistinctUntilChanged = () =>
  pipe(
    select(selectorCoreLogin),
    distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b))
  );

export const selectCoreInstanceDistinctUntilChanged = () =>
  pipe(
    select(selectorCoreSelectedInstance),
    distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b))
  );

const isFeetMeDevice = (subType: any): boolean => {
  return subType === 'model-feetme';
};

const objectToString = (objectToParse: any): string => {
  return Object.entries(objectToParse)
    .map(([key, value]) => `${key}=${value}`)
    .join('&');
};
